import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Activity, 
  Target, 
  DollarSign, 
  ArrowRight,
  Stethoscope,
  AlertTriangle,
  CheckCircle2,
  Clock
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';

interface AgencyKpiWidgetProps {
  agencyId: string;
  agencyName?: string;
}

interface KpiData {
  // Current month metrics
  currentMonthSessions: number;
  currentMonthSavings: number;
  currentMonthDiversionRate: number;
  // Previous month for comparison
  previousMonthSessions: number;
  previousMonthSavings: number;
  previousMonthDiversionRate: number;
  // Totals
  totalPatients: number;
  activePatients: number;
  totalSessions: number;
  redFlagCases: number;
  // Breakdown
  homeCases: number;
  pcpCases: number;
  urgentCareCases: number;
  erCases: number;
}

// Default costs for savings calculation
const COSTS = {
  HOME: 0,
  PCP: 150,
  URGENT_CARE: 500,
  ER_NOW: 2500,
};

export default function AgencyKpiWidget({ agencyId, agencyName }: AgencyKpiWidgetProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<KpiData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadKpiData = async () => {
      if (!agencyId) {
        setLoading(false);
        return;
      }

      try {
        const now = new Date();
        const currentMonthStart = startOfMonth(now);
        const currentMonthEnd = endOfMonth(now);
        const previousMonthStart = startOfMonth(subMonths(now, 1));
        const previousMonthEnd = endOfMonth(subMonths(now, 1));

        // Fetch all data in parallel for efficiency
        const [
          patientsResult,
          currentMonthResult,
          previousMonthResult,
          totalSessionsResult
        ] = await Promise.all([
          // Total patients for this agency
          supabase
            .from('patients')
            .select('id, is_active')
            .eq('agency_id', agencyId),
          
          // Current month analytics
          supabase.rpc('get_cost_savings_analytics', {
            _agency_id: agencyId,
            _start_date: currentMonthStart.toISOString(),
            _end_date: currentMonthEnd.toISOString()
          }),
          
          // Previous month analytics
          supabase.rpc('get_cost_savings_analytics', {
            _agency_id: agencyId,
            _start_date: previousMonthStart.toISOString(),
            _end_date: previousMonthEnd.toISOString()
          }),
          
          // All-time sessions with red flag count
          supabase
            .from('triage_sessions')
            .select('id, red_flags, recommendation')
            .eq('agency_id', agencyId)
            .not('completed_at', 'is', null)
        ]);

        // Process patients data
        const patients = patientsResult.data || [];
        const totalPatients = patients.length;
        const activePatients = patients.filter(p => p.is_active).length;

        // Process current month data
        const currentData = currentMonthResult.data as Record<string, number> | null;
        const currentMonthSessions = currentData?.totalSessions || 0;
        const currentHomeCases = currentData?.homeCases || 0;
        const currentPcpCases = currentData?.pcpCases || 0;
        const currentUrgentCases = currentData?.urgentCareCases || 0;
        const currentErCases = currentData?.erCases || 0;
        const currentDiverted = currentHomeCases + currentPcpCases + currentUrgentCases;
        const currentMonthDiversionRate = currentMonthSessions > 0 
          ? (currentDiverted / currentMonthSessions) * 100 
          : 0;
        const currentMonthSavings = 
          (currentHomeCases * (COSTS.ER_NOW - COSTS.HOME)) +
          (currentPcpCases * (COSTS.ER_NOW - COSTS.PCP)) +
          (currentUrgentCases * (COSTS.ER_NOW - COSTS.URGENT_CARE));

        // Process previous month data
        const prevData = previousMonthResult.data as Record<string, number> | null;
        const previousMonthSessions = prevData?.totalSessions || 0;
        const prevHomeCases = prevData?.homeCases || 0;
        const prevPcpCases = prevData?.pcpCases || 0;
        const prevUrgentCases = prevData?.urgentCareCases || 0;
        const prevDiverted = prevHomeCases + prevPcpCases + prevUrgentCases;
        const previousMonthDiversionRate = previousMonthSessions > 0 
          ? (prevDiverted / previousMonthSessions) * 100 
          : 0;
        const previousMonthSavings = 
          (prevHomeCases * (COSTS.ER_NOW - COSTS.HOME)) +
          (prevPcpCases * (COSTS.ER_NOW - COSTS.PCP)) +
          (prevUrgentCases * (COSTS.ER_NOW - COSTS.URGENT_CARE));

        // Process all-time session data
        const allSessions = totalSessionsResult.data || [];
        const totalSessions = allSessions.length;
        const redFlagCases = allSessions.filter(s => s.red_flags).length;
        
        // All-time breakdown
        const homeCases = allSessions.filter(s => s.recommendation === 'HOME').length;
        const pcpCases = allSessions.filter(s => s.recommendation === 'PCP').length;
        const urgentCareCases = allSessions.filter(s => s.recommendation === 'URGENT_CARE').length;
        const erCases = allSessions.filter(s => s.recommendation === 'ER_NOW').length;

        setData({
          currentMonthSessions,
          currentMonthSavings,
          currentMonthDiversionRate,
          previousMonthSessions,
          previousMonthSavings,
          previousMonthDiversionRate,
          totalPatients,
          activePatients,
          totalSessions,
          redFlagCases,
          homeCases,
          pcpCases,
          urgentCareCases,
          erCases
        });
      } catch (error) {
        console.error('Error loading KPI data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadKpiData();
  }, [agencyId]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getChangePercentage = (current: number, previous: number): number => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  const ChangeIndicator = ({ current, previous, inverted = false }: { 
    current: number; 
    previous: number; 
    inverted?: boolean;
  }) => {
    const change = getChangePercentage(current, previous);
    const isPositive = inverted ? change < 0 : change > 0;
    const isNeutral = Math.abs(change) < 0.5;
    
    if (isNeutral) {
      return <span className="text-xs text-muted-foreground">No change</span>;
    }
    
    return (
      <span className={`text-xs flex items-center gap-0.5 ${isPositive ? 'text-emerald-600' : 'text-amber-600'}`}>
        {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
        {Math.abs(change).toFixed(1)}%
      </span>
    );
  };

  const handleViewAnalytics = () => {
    const params = new URLSearchParams();
    if (searchParams.get('impersonate')) {
      params.set('impersonate', searchParams.get('impersonate')!);
    }
    if (searchParams.get('aid')) {
      params.set('aid', searchParams.get('aid')!);
    }
    const queryString = params.toString();
    navigate(`/admin/analytics${queryString ? `?${queryString}` : ''}`);
  };

  if (loading) {
    return (
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            Agency Performance KPIs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-20 bg-muted rounded-lg"></div>
              ))}
            </div>
            <div className="h-32 bg-muted rounded-lg"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return null;
  }

  const totalDiverted = data.homeCases + data.pcpCases + data.urgentCareCases;
  const allTimeDiversionRate = data.totalSessions > 0 
    ? (totalDiverted / data.totalSessions) * 100 
    : 0;
  const allTimeSavings = 
    (data.homeCases * (COSTS.ER_NOW - COSTS.HOME)) +
    (data.pcpCases * (COSTS.ER_NOW - COSTS.PCP)) +
    (data.urgentCareCases * (COSTS.ER_NOW - COSTS.URGENT_CARE));

  return (
    <Card className="shadow-card border-primary/20">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              Agency Performance KPIs
            </CardTitle>
            <CardDescription>
              {agencyName ? `Key metrics for ${agencyName}` : 'Key performance indicators'}
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={handleViewAnalytics}>
            Full Analytics <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Month Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Monthly Savings */}
          <div className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/30 rounded-xl p-4 border border-emerald-200 dark:border-emerald-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                {format(new Date(), 'MMMM')} Savings
              </span>
              <DollarSign className="h-4 w-4 text-emerald-600" />
            </div>
            <div className="text-2xl font-bold text-emerald-800 dark:text-emerald-300">
              {formatCurrency(data.currentMonthSavings)}
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs text-emerald-600 dark:text-emerald-500">
                vs last month
              </span>
              <ChangeIndicator 
                current={data.currentMonthSavings} 
                previous={data.previousMonthSavings} 
              />
            </div>
          </div>

          {/* Monthly Sessions */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-700 dark:text-blue-400">
                {format(new Date(), 'MMMM')} Sessions
              </span>
              <Stethoscope className="h-4 w-4 text-blue-600" />
            </div>
            <div className="text-2xl font-bold text-blue-800 dark:text-blue-300">
              {data.currentMonthSessions.toLocaleString()}
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs text-blue-600 dark:text-blue-500">
                vs last month
              </span>
              <ChangeIndicator 
                current={data.currentMonthSessions} 
                previous={data.previousMonthSessions} 
              />
            </div>
          </div>

          {/* Diversion Rate */}
          <div className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-950/30 dark:to-violet-950/30 rounded-xl p-4 border border-purple-200 dark:border-purple-800">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-purple-700 dark:text-purple-400">
                Diversion Rate
              </span>
              <TrendingUp className="h-4 w-4 text-purple-600" />
            </div>
            <div className="text-2xl font-bold text-purple-800 dark:text-purple-300">
              {data.currentMonthDiversionRate.toFixed(1)}%
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs text-purple-600 dark:text-purple-500">
                vs last month
              </span>
              <ChangeIndicator 
                current={data.currentMonthDiversionRate} 
                previous={data.previousMonthDiversionRate} 
              />
            </div>
          </div>
        </div>

        {/* All-Time Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Total Patients</span>
            </div>
            <div className="text-lg font-semibold">{data.totalPatients.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">
              {data.activePatients} active
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Activity className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">All-Time Sessions</span>
            </div>
            <div className="text-lg font-semibold">{data.totalSessions.toLocaleString()}</div>
            <div className="text-xs text-emerald-600">
              {formatCurrency(allTimeSavings)} saved
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Cases Diverted</span>
            </div>
            <div className="text-lg font-semibold">{totalDiverted.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">
              {allTimeDiversionRate.toFixed(1)}% rate
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <span className="text-xs text-muted-foreground">Red Flag Cases</span>
            </div>
            <div className="text-lg font-semibold">{data.redFlagCases.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">
              {data.totalSessions > 0 
                ? ((data.redFlagCases / data.totalSessions) * 100).toFixed(1) 
                : 0}% of total
            </div>
          </div>
        </div>

        {/* Recommendation Breakdown */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground">Recommendation Breakdown</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <span className="text-xs w-20 text-muted-foreground">Home Care</span>
              <Progress 
                value={data.totalSessions > 0 ? (data.homeCases / data.totalSessions) * 100 : 0} 
                className="h-2 flex-1"
              />
              <span className="text-xs font-mono w-16 text-right">
                {data.homeCases.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs w-20 text-muted-foreground">PCP Visit</span>
              <Progress 
                value={data.totalSessions > 0 ? (data.pcpCases / data.totalSessions) * 100 : 0} 
                className="h-2 flex-1"
              />
              <span className="text-xs font-mono w-16 text-right">
                {data.pcpCases.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs w-20 text-muted-foreground">Urgent Care</span>
              <Progress 
                value={data.totalSessions > 0 ? (data.urgentCareCases / data.totalSessions) * 100 : 0} 
                className="h-2 flex-1"
              />
              <span className="text-xs font-mono w-16 text-right">
                {data.urgentCareCases.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs w-20 text-red-600">ER Required</span>
              <Progress 
                value={data.totalSessions > 0 ? (data.erCases / data.totalSessions) * 100 : 0} 
                className="h-2 flex-1 [&>div]:bg-red-500"
              />
              <span className="text-xs font-mono w-16 text-right text-red-600">
                {data.erCases.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
